import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import ConfirmModal from '../shared/ConfirmModal';
import {
  FiCalendar, FiCheckCircle, FiRefreshCw, FiAlertCircle,
  FiCheck, FiX, FiClock,
  FiSearch, FiBarChart2, FiUsers,
  FiExternalLink, FiTrendingDown,
  FiChevronLeft, FiChevronRight, FiLayers,
  FiPlus, FiFileText, FiAward, FiArrowLeft
} from 'react-icons/fi';
import ManagerAddLeaveModal from './ManagerAddLeaveModal';
import CreditEarnedLeaveModal from './CreditEarnedLeaveModal';
import EarnedLeaveDetailsModal from './EarnedLeaveDetailsModal';
import { fmtDate, getCurrentFinancialYear } from '../../utils/istDate';

/* ─── Axios instance ────────────────────────────────────────────────── */
const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const initials = (name = '') =>
  name.split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '??';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function toDateStr(raw) {
  if (!raw) return '';
  if (typeof raw === 'string') return raw.split('T')[0];
  if (Array.isArray(raw)) {
    const [y, m, d] = raw;
    return `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  }
  try {
    const d = new Date(raw);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  } catch { return ''; }
}

/* ═══════════════════════════════════════════════════════════════════════
   SUMMARY MODAL — per-subordinate leave balance, calendar & history
═══════════════════════════════════════════════════════════════════════ */
function SummaryModal({ emp, onClose, onAddLeave, onCreditEarnedLeave }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [earnedLeaves, setEarnedLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [histTab, setHistTab] = useState('PENDING');
  const [calDate, setCalDate] = useState(new Date());
  const year = getCurrentFinancialYear();

  useEffect(() => {
    if (!emp) return;
    setLoading(true);
    setData(null);
    setHistory([]);
    setEarnedLeaves([]);
    const curYear = getCurrentFinancialYear();
    Promise.allSettled([
      api.get(`/leave/subordinate/${emp.employeeId}/monthly-balance/${curYear}`),
      api.get(`/leave/subordinate/${emp.employeeId}`),
      api.get(`/leave/subordinate/${emp.employeeId}/earned-leaves`),
      api.get('/holidays'),
    ]).then(([balRes, histRes, elRes, holRes]) => {
      if (balRes.status === 'fulfilled') setData(balRes.value.data?.data ?? balRes.value.data);
      if (histRes.status === 'fulfilled') {
        const raw = histRes.value.data?.data ?? histRes.value.data ?? [];
        setHistory(Array.isArray(raw) ? raw : []);
      }
      if (elRes.status === 'fulfilled') {
        const raw = elRes.value.data?.data ?? elRes.value.data ?? [];
        setEarnedLeaves(Array.isArray(raw) ? raw : []);
      }
      if (holRes.status === 'fulfilled') {
        const raw = holRes.value.data;
        setHolidays(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
      }
    }).catch(() => toast.error('Could not load summary'))
      .finally(() => setLoading(false));
  }, [emp, year]);

  const pendingList  = useMemo(() => history.filter(l => l.status === 'PENDING'), [history]);
  const approvedList = useMemo(() => history.filter(l => l.status === 'APPROVED'), [history]);
  const rejectedList = useMemo(() => history.filter(l => l.status === 'REJECTED' || l.status === 'CANCELLED'), [history]);

  // Calendar helpers
  const changeMonth = (offset) => {
    setCalDate(prev => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + offset);
      return next;
    });
  };

  const getHoliday = useCallback((dateStr) => {
    return holidays.find(h => toDateStr(h.date || h.holidayDate) === dateStr);
  }, [holidays]);

  const getLeaveForDate = useCallback((dateStr) => {
    return history.find(l => {
      const s = toDateStr(l.startDate);
      const e = toDateStr(l.endDate);
      return s && e && dateStr >= s && dateStr <= e;
    });
  }, [history]);

  const calendarCells = useMemo(() => {
    const year = calDate.getFullYear();
    const month = calDate.getMonth();
    const firstDayIndex = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrev = new Date(year, month, 0).getDate();
    const cells = [];

    const makeKey = (y, m, d) => `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

    // prev month padding
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = daysInPrev - i;
      const d = new Date(year, month - 1, dayNum);
      cells.push({ dateStr: makeKey(d.getFullYear(), d.getMonth(), dayNum), dayNum, isCurrentMonth: false, isWeekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    // current month
    for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
      const d = new Date(year, month, dayNum);
      cells.push({ dateStr: makeKey(year, month, dayNum), dayNum, isCurrentMonth: true, isWeekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    // next month padding
    const remaining = (7 - (cells.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      cells.push({ dateStr: makeKey(d.getFullYear(), d.getMonth(), i), dayNum: i, isCurrentMonth: false, isWeekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    return cells;
  }, [calDate]);

  if (!emp) return null;

  const modalNode = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #008cd6, #0ea5e9)' }} className="p-4 sm:px-6 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/20 flex items-center justify-center font-bold text-sm sm:text-base text-white shrink-0">
              {initials(emp.fullName || emp.name)}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-white text-sm truncate">{emp.fullName || emp.name}</p>
              <p className="text-xs text-sky-100 truncate">{emp.employeeId} · {emp.jobTitle || emp.department || 'Employee'} · Leave Summary {year}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end sm:self-auto flex-wrap">
            {onCreditEarnedLeave && (
              <button
                onClick={() => {
                  onClose();
                  onCreditEarnedLeave(emp);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 transition-colors shadow-xs cursor-pointer"
              >
                <FiAward size={13} /> Credit Earned
              </button>
            )}
            {onAddLeave && (
              <button
                onClick={() => {
                  onClose();
                  onAddLeave(emp);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-white/20 hover:bg-white/30 transition-colors shadow-xs cursor-pointer"
              >
                <FiPlus size={13} /> Add Leave
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <FiX size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-3.5 sm:p-5 bg-slate-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-2 text-slate-400">
              <FiRefreshCw className="animate-spin" size={22} />
              <p className="text-sm font-medium">Loading leave summary…</p>
            </div>
          ) : !data ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-slate-400">
              <FiAlertCircle size={22} />
              <p className="text-sm">No balance data available</p>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-5">

              {/* ── Balance Stats ─────────────────────────── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Annual */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                    <p className="text-[11px] font-bold text-[#008cd6] uppercase tracking-widest flex items-center gap-1.5">
                      <FiLayers size={12} /> Annual Overview
                    </p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-y sm:divide-y-0 divide-slate-100">
                    {[
                      { label: 'Limit', value: data.annualLimit ?? '—' },
                      { label: 'Earned (Year)', value: data.earnedLeave ?? 0 },
                      { label: 'Paid Taken', value: data.paidTakenYear ?? 0 },
                      { label: 'Remaining', value: data.totalPaidLeavesAvailableYear ?? data.currentAvailableBalance ?? 0, highlight: true },
                    ].map(({ label, value, highlight }) => (
                      <div key={label} className={`p-3 text-center ${highlight ? 'bg-emerald-50' : ''}`}>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</p>
                        <p className={`text-xl font-black ${highlight ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Monthly */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                    <p className="text-[11px] font-bold text-[#008cd6] uppercase tracking-widest flex items-center gap-1.5">
                      <FiCalendar size={12} /> This Month
                    </p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-y sm:divide-y-0 divide-slate-100">
                    {[
                      { label: 'Monthly Cap', value: data.monthlyPaidLeaveLimit ?? 3 },
                      { label: 'Paid Used', value: data.paidTakenMonth ?? 0 },
                      { label: 'All Taken', value: data.totalTakenMonth ?? 0 },
                      { label: 'Slots Left', value: data.totalPaidLeavesAvailableMonth ?? 0, highlight: true },
                    ].map(({ label, value, highlight }) => (
                      <div key={label} className={`p-3 text-center ${highlight ? 'bg-sky-50' : ''}`}>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</p>
                        <p className={`text-xl font-black ${highlight ? 'text-[#008cd6]' : 'text-slate-900'}`}>{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* LOP banner */}
              {((data.lopLeavesMonth || 0) > 0 || (data.lopLeavesYear || 0) > 0) && (
                <div className="rounded-xl p-4 text-white bg-slate-900 flex items-center justify-between gap-6 flex-wrap">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5 shrink-0">
                    <FiTrendingDown size={12} className="text-rose-400" /> Loss of Pay (LOP)
                  </p>
                  <div className="flex gap-8 text-center">
                    <div>
                      <p className="text-2xl font-black text-rose-300">
                        {data.lopLeavesMonth ?? 0} <span className="text-xs font-normal text-slate-400">days</span>
                      </p>
                      <p className="text-[10px] text-slate-400">This Month</p>
                    </div>
                    <div>
                      <p className="text-2xl font-black text-rose-300">
                        {data.lopLeavesYear ?? 0} <span className="text-xs font-normal text-slate-400">days</span>
                      </p>
                      <p className="text-[10px] text-slate-400">This Year (FY)</p>
                    </div>
                    {data.lopDeductionAmount != null && (
                      <div>
                        <p className="text-xl font-black">₹{Number(data.lopDeductionAmount).toLocaleString('en-IN')}</p>
                        <p className="text-[10px] text-slate-400">Est. Deduction</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Earned Leaves Details & History ───────── */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-2xs">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                  <p className="text-[11px] font-bold text-slate-700 uppercase tracking-widest flex items-center gap-1.5">
                    <FiAward size={13} className="text-slate-500" /> Earned Leaves Details &amp; History
                  </p>
                  <span className="text-xs font-bold text-slate-700 font-mono">
                    {data.earnedLeave ?? 0} days balance
                  </span>
                </div>
                {earnedLeaves.length === 0 ? (
                  <div className="p-5 text-center text-slate-400">
                    <p className="text-xs">No earned leave credit events recorded yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 text-xs">
                    {earnedLeaves.map((el) => (
                      <div key={el.id} className="p-3.5 hover:bg-slate-50/50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900">Duty on {fmtDate(el.effectiveDate)}</span>
                            <span className="px-2 py-0.5 rounded-full font-bold bg-slate-100 text-slate-700 text-[10px] border border-slate-200">
                              {el.creditDays} day{el.creditDays > 1 ? 's' : ''}
                            </span>
                          </div>
                          <p className="text-slate-600 text-[11px] mt-0.5">
                            <strong>Reason:</strong> {el.remarks || 'Weekend or holiday shift completed'}
                          </p>
                        </div>
                        <div className="text-left sm:text-right text-[10px] text-slate-400 shrink-0">
                          <p>Credited by: <strong className="text-slate-600">{el.createdBy || 'Manager'}</strong></p>
                          {el.createdAt && <p className="mt-0.5">{fmtDate(el.createdAt)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Calendar ──────────────────────────────── */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Calendar header */}
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <p className="text-sm font-bold text-slate-800">
                    {MONTH_NAMES[calDate.getMonth()]} {calDate.getFullYear()}
                  </p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => changeMonth(-1)} className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors">
                      <FiChevronLeft size={14} />
                    </button>
                    <button
                      onClick={() => setCalDate(new Date())}
                      className="px-2 py-1 rounded-lg text-[11px] font-bold text-[#008cd6] hover:bg-sky-50 transition-colors"
                    >
                      Today
                    </button>
                    <button onClick={() => changeMonth(1)} className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors">
                      <FiChevronRight size={14} />
                    </button>
                  </div>
                </div>

                {/* Day labels & Calendar cells */}
                <div className="overflow-x-auto">
                  <div className="min-w-[440px]">
                    <div className="grid grid-cols-7 border-b border-slate-100">
                  {DAY_NAMES.map(d => (
                    <div key={d} className="py-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      {d}
                    </div>
                  ))}
                </div>

                {/* Calendar cells */}
                <div className="grid grid-cols-7 gap-px bg-slate-100 p-px">
                  {calendarCells.map(({ dateStr, dayNum, isCurrentMonth, isWeekend }) => {
                    const leave = getLeaveForDate(dateStr);
                    const holiday = getHoliday(dateStr);
                    const todayStr = new Date().toISOString().split('T')[0];
                    const isToday = dateStr === todayStr;

                    if (!isCurrentMonth) {
                      return (
                        <div key={dateStr} className="min-h-[56px] p-1.5 bg-slate-50 flex flex-col">
                          <span className="text-[10px] font-semibold text-slate-300">{dayNum}</span>
                        </div>
                      );
                    }

                    if (holiday) {
                      return (
                        <div key={dateStr} className="min-h-[56px] p-1.5 bg-teal-50 flex flex-col gap-0.5">
                          <span className={`text-[10px] font-bold ${isToday ? 'bg-teal-600 text-white w-5 h-5 rounded-full flex items-center justify-center' : 'text-teal-700'}`}>{dayNum}</span>
                          <span className="text-[9px] font-semibold text-teal-600 leading-tight truncate">🎉 {holiday.name || 'Holiday'}</span>
                        </div>
                      );
                    }

                    if (leave) {
                      const isPending = leave.status === 'PENDING';
                      const isUnpaid = (leave.unpaidDays || 0) > 0 && (leave.paidDays || 0) === 0;
                      let bgClass = 'bg-emerald-50/90 border border-emerald-300';
                      let textClass = 'text-emerald-950';
                      let dotClass = 'bg-emerald-500';

                      if (isUnpaid || leave.status === 'REJECTED') {
                        bgClass = 'bg-rose-50/90 border border-rose-300';
                        textClass = 'text-rose-950';
                        dotClass = 'bg-rose-500';
                      } else if (isPending) {
                        bgClass = 'bg-amber-50/80 border border-amber-300';
                        textClass = 'text-amber-950';
                        dotClass = 'bg-amber-500';
                      }

                      return (
                        <div key={dateStr} className={`min-h-[56px] p-1.5 ${bgClass} flex flex-col gap-0.5`}>
                          <span className={`text-[10px] font-bold ${isToday ? `${dotClass} text-white w-5 h-5 rounded-full flex items-center justify-center` : textClass}`}>{dayNum}</span>
                          <span className={`text-[9px] font-bold ${textClass} leading-tight truncate`}>{leave.leaveType}</span>
                          <span className={`w-1.5 h-1.5 rounded-full ${dotClass} mt-auto`} />
                        </div>
                      );
                    }

                    if (isWeekend) {
                      return (
                        <div key={dateStr} className="min-h-[56px] p-1.5 bg-slate-50/80 flex flex-col">
                          <span className="text-[10px] font-semibold text-slate-300">{dayNum}</span>
                          <span className="text-[9px] text-slate-300 mt-auto">Off</span>
                        </div>
                      );
                    }

                    return (
                      <div key={dateStr} className={`min-h-[56px] p-1.5 bg-white flex flex-col hover:bg-slate-50 transition-colors`}>
                        <span className={`text-[10px] font-bold ${isToday ? 'bg-[#008cd6] text-white w-5 h-5 rounded-full flex items-center justify-center' : 'text-slate-700'}`}>{dayNum}</span>
                      </div>
                    );
                  })}
                </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-x-5 gap-y-1.5 flex-wrap text-[11px] font-medium text-slate-600">
                  <span className="text-slate-400 text-[10px] uppercase tracking-wider font-bold mr-1">Legend:</span>
                  <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"/><span className="text-slate-700 font-semibold">Paid Leave</span></div>
                  <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-rose-500 shrink-0"/><span className="text-slate-700 font-semibold">LOP / Unpaid Leave</span></div>

                  <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-slate-400 shrink-0"/><span className="text-slate-700 font-semibold">Past Leave</span></div>
                  <div className="flex items-center gap-2 text-teal-700"><span className="w-2.5 h-2.5 rounded-full bg-teal-500 shrink-0"/><span>Holiday</span></div>
                </div>
              </div>

              {/* ── Leave History ──────────────────────────── */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                  {[
                    { key: 'PENDING',  label: `Pending (${pendingList.length})`,  color: '#92400e', bg: '#fffbeb', border: '#fcd34d' },
                    { key: 'APPROVED', label: `Approved (${approvedList.length})`, color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
                    { key: 'REJECTED', label: `Rejected (${rejectedList.length})`, color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
                  ].map(t => (
                    <button
                      key={t.key}
                      onClick={() => setHistTab(t.key)}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold border transition-all"
                      style={histTab === t.key
                        ? { background: t.bg, color: t.color, borderColor: t.border }
                        : { background: 'transparent', color: '#6b7280', borderColor: 'transparent' }
                      }
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="divide-y divide-slate-50 max-h-52 overflow-y-auto">
                  {(histTab === 'PENDING' ? pendingList : histTab === 'APPROVED' ? approvedList : rejectedList).length === 0 ? (
                    <p className="text-xs text-slate-400 py-6 text-center">No {histTab.toLowerCase()} leaves</p>
                  ) : (
                    (histTab === 'PENDING' ? pendingList : histTab === 'APPROVED' ? approvedList : rejectedList).map(l => (
                      <div key={l.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full border" style={{ background: '#f0f9ff', color: '#008cd6', borderColor: '#bae6fd' }}>{l.leaveType}</span>
                          {l.addedByManager && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                              Added by Manager
                            </span>
                          )}
                          <span className="text-xs text-slate-500">{fmtDate(l.startDate)} → {fmtDate(l.endDate)}</span>
                        </div>
                        <span className="text-sm font-bold text-slate-700">{l.daysCount}d
                          <span className="text-xs font-normal text-slate-400 ml-1">{l.paidDays ?? 0}P/{l.unpaidDays ?? 0}U</span>
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalNode, document.body) : modalNode;
}

/* ═══════════════════════════════════════════════════════════════════════
   LEAVE REQUEST CARD
═══════════════════════════════════════════════════════════════════════ */
function LeaveCard({ leave, onApprove, onReject, onReverseReject, onOpenDetail, isSuperadmin }) {
  const name = leave.employeeName || leave.employeeUsername || `#${leave.employeeId}`;
  const isPending = leave.status === 'PENDING';
  const isRejected = leave.status === 'REJECTED';

  const statusConfig = {
    PENDING:   { label: 'Pending',   bg: '#f0f9ff', color: '#008cd6', dot: '#008cd6' },
    APPROVED:  { label: 'Approved',  bg: '#f0fdf4', color: '#15803d', dot: '#22c55e' },
    REJECTED:  { label: 'Rejected',  bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444' },
    CANCELLED: { label: 'Cancelled', bg: '#f9fafb', color: '#6b7280', dot: '#9ca3af' },
  };
  const sc = statusConfig[leave.status] || statusConfig.PENDING;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:px-6 sm:py-5 flex items-start gap-3 sm:gap-4 shadow-sm hover:shadow-md hover:border-sky-300 transition-all">
      {/* Avatar */}
      <div
        onClick={() => onOpenDetail && onOpenDetail(leave)}
        className="w-9 h-9 sm:w-10 sm:h-10 mt-0.5 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
        style={{ background: 'linear-gradient(135deg, #008cd6, #38bdf8)' }}
        title="View detailed info"
      >
        {initials(name)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Name row */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap mb-1">
          <span
            onClick={() => onOpenDetail && onOpenDetail(leave)}
            className="font-bold text-gray-900 text-sm sm:text-base cursor-pointer hover:text-[#008cd6] transition-colors"
          >{name}</span>

          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-bold" style={{ background: sc.bg, color: sc.color }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: sc.dot, animation: isPending ? 'pulse 2s infinite' : 'none' }} />
              {sc.label}
            </span>
            <span className="text-xs font-bold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full border shadow-sm" style={{ background: '#f0f9ff', color: '#008cd6', borderColor: '#bae6fd' }}>
              {leave.leaveType}
            </span>
            {leave.isHalfDay && (
              <span className="text-xs font-bold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full border shadow-sm bg-indigo-50 text-indigo-700 border-indigo-200">
                Half Day ({leave.halfDaySession === 'SECOND_HALF' ? 'Second Half' : 'First Half'})
              </span>
            )}
            {leave.addedByManager && (
              <span className="text-[11px] sm:text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                Added by Manager
              </span>
            )}
            {(leave.unpaidDays || 0) > 0 && (
              <span className="text-xs font-bold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-white shadow-sm" style={{ background: '#111827' }}>LOP</span>
            )}
          </div>

          {leave.appliedDate && (
            <span className="flex items-center gap-1 text-[11px] sm:text-xs text-gray-400 sm:ml-auto w-full sm:w-auto mt-0.5 sm:mt-0">
              <FiClock size={12} /> Applied {fmtDate(leave.appliedDate)}
            </span>
          )}
        </div>

        {/* Sub-header */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2.5 sm:mb-3 flex-wrap">
          {leave.employeeId && <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[11px]">#{leave.employeeId}</span>}
          {(leave.jobTitle || leave.department) && (
            <span className="font-medium text-[11px] sm:text-xs">
              {leave.jobTitle} {leave.jobTitle && leave.department && <span className="text-gray-300 px-1">•</span>} {leave.department}
            </span>
          )}
        </div>

        {/* Dates */}
        <div className="flex items-center gap-2.5 sm:gap-5 mt-1.5 sm:mt-2 text-xs sm:text-sm text-gray-500 flex-wrap">
          <span className="flex items-center gap-1.5 text-gray-700 font-medium">
            <FiCalendar style={{ color: '#008cd6' }} size={14} />
            {fmtDate(leave.startDate)}{leave.startDate !== leave.endDate && ` – ${fmtDate(leave.endDate)}`}
            <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded-lg ml-1">
              {leave.daysCount != null ? leave.daysCount : (leave.isHalfDay ? 0.5 : 1)}d
            </span>
          </span>
          {leave.paidDays != null && (
            <span className="font-semibold text-gray-600 bg-sky-50 px-2 py-0.5 rounded-lg border border-sky-100 text-xs">
              {leave.paidDays} Paid {(leave.unpaidDays || 0) > 0 && <span className="text-red-600">/ {leave.unpaidDays} LOP</span>}
            </span>
          )}
        </div>

        {/* Reason */}
        {(leave.reason || leave.remarks) && (
          <div className="mt-2.5 sm:mt-3 bg-gray-50 border border-gray-200 rounded-lg p-2.5 sm:p-3 text-xs sm:text-sm flex flex-col gap-1.5">
            {leave.reason && <p className="text-gray-700 leading-relaxed"><strong className="text-gray-900 font-bold mr-1.5">Reason:</strong>{leave.reason}</p>}
            {leave.remarks && <p className="text-gray-700 leading-relaxed"><strong className="text-gray-900 font-bold mr-1.5">Remarks:</strong>{leave.remarks}</p>}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-3 mt-3.5 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
          <button
            onClick={() => onOpenDetail && onOpenDetail(leave)}
            className="flex items-center justify-center gap-1.5 px-3.5 py-2 sm:py-1.5 rounded-lg text-xs font-bold border border-slate-200 text-slate-700 bg-slate-50 hover:bg-white hover:border-sky-300 hover:text-[#008cd6] transition-all shadow-sm cursor-pointer w-full sm:w-auto"
          >
            <FiExternalLink size={13} style={{ color: '#008cd6' }} /> Detailed Info →
          </button>

          <div className="grid grid-cols-2 sm:flex items-center gap-2 sm:ml-auto w-full sm:w-auto">
            {isPending && (
              <>
                <button
                  onClick={() => onApprove(leave)}
                  className="flex items-center justify-center gap-1.5 px-4 py-2 sm:py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm hover:opacity-90 cursor-pointer text-white"
                  style={{ background: 'linear-gradient(135deg, #008cd6, #0ea5e9)' }}
                >
                  <FiCheck size={13} /> Approve
                </button>
                <button
                  onClick={() => onReject(leave)}
                  className="flex items-center justify-center gap-1.5 px-4 py-2 sm:py-1.5 rounded-lg text-xs font-bold border transition-all hover:bg-red-50 hover:border-red-300 hover:text-red-600 shadow-sm cursor-pointer"
                  style={{ color: '#374151', borderColor: '#d1d5db' }}
                >
                  <FiX size={13} /> Reject
                </button>
              </>
            )}
            {isRejected && onReverseReject && (
              <button
                onClick={() => onReverseReject(leave)}
                className="col-span-2 sm:col-span-1 flex items-center justify-center gap-1.5 px-4 py-2 sm:py-1.5 rounded-lg text-xs font-bold border transition-all hover:bg-orange-100 shadow-sm cursor-pointer"
                style={{ color: '#c2410c', borderColor: '#fed7aa', background: '#fff7ed' }}
              >
                <FiRefreshCw size={13} /> Reverse Rejection
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MAIN PAGE — ManagerLeaveApprovals
═══════════════════════════════════════════════════════════════════════ */
const ManagerLeaveApprovals = () => {
  const navigate = useNavigate();

  // Data state
  const [subordinates, setSubordinates] = useState([]);
  const [allLeaves, setAllLeaves]       = useState([]);
  const [loadingSubs, setLoadingSubs]   = useState(false);
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [error, setError]               = useState('');

  // UI state
  const [mainView, setMainView]         = useState('leaves'); // 'leaves' | 'team'
  const [activeTab, setActiveTab]       = useState('PENDING');
  const [searchTerm, setSearchTerm]     = useState('');
  const [empSearch, setEmpSearch]       = useState('');
  const [actionRemark, setActionRemark] = useState('');
  const [confirm, setConfirm]           = useState({ open: false, leave: null, status: null, loading: false });

  // Add Leave state
  const [addLeaveModalOpen, setAddLeaveModalOpen] = useState(false);
  const [selectedSubForLeave, setSelectedSubForLeave] = useState(null);

  // Credit Earned Leave state
  const [creditEarnedModalOpen, setCreditEarnedModalOpen] = useState(false);
  const [selectedSubForEarned, setSelectedSubForEarned] = useState(null);

  const [summaryModalEmp, setSummaryModalEmp]     = useState(null);
  const [earnedDetailsEmp, setEarnedDetailsEmp]   = useState(null);

  const openAddLeave = (emp = null) => {
    setSelectedSubForLeave(emp);
    setAddLeaveModalOpen(true);
  };

  const openCreditEarned = (emp = null) => {
    setSelectedSubForEarned(emp);
    setCreditEarnedModalOpen(true);
  };

  const currentUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const isSuperadmin = ['SUPERADMIN', 'TESTER'].includes(String(currentUser.role || '').toUpperCase());

  /* ── Fetch direct subordinates ────────────────────────────── */
  const fetchSubordinates = useCallback(async () => {
    setLoadingSubs(true);
    try {
      const res = await api.get('/leave/subordinates');
      const raw = res.data?.data ?? res.data ?? [];
      setSubordinates(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error('Failed to load subordinates', err);
      toast.error('Could not load team members');
    } finally {
      setLoadingSubs(false);
    }
  }, []);

  /* ── Fetch all subordinate leaves ─────────────────────────── */
  const fetchLeaves = useCallback(async () => {
    setLoadingLeaves(true);
    setError('');
    try {
      const res = await api.get('/leave/subordinates/leaves');
      const raw = res.data?.data ?? res.data ?? [];
      setAllLeaves(Array.isArray(raw) ? raw : []);
    } catch (err) {
      console.error('Failed to load leaves', err);
      setError('Failed to load leave data. Please refresh.');
    } finally {
      setLoadingLeaves(false);
    }
  }, []);

  useEffect(() => {
    fetchSubordinates();
    fetchLeaves();
  }, [fetchSubordinates, fetchLeaves]);

  /* ── Counts ───────────────────────────────────────────────── */
  const counts = React.useMemo(() => ({
    PENDING:  allLeaves.filter(l => l.status === 'PENDING').length,
    APPROVED: allLeaves.filter(l => l.status === 'APPROVED').length,
    REJECTED: allLeaves.filter(l => l.status === 'REJECTED' || l.status === 'CANCELLED').length,
  }), [allLeaves]);

  /* ── Filtered leave list ──────────────────────────────────── */
  const filteredLeaves = React.useMemo(() => {
    let list = allLeaves.filter(l => {
      if (activeTab === 'PENDING')  return l.status === 'PENDING';
      if (activeTab === 'APPROVED') return l.status === 'APPROVED';
      if (activeTab === 'REJECTED') return l.status === 'REJECTED' || l.status === 'CANCELLED';
      return true;
    });
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(l =>
        (l.employeeName || '').toLowerCase().includes(q) ||
        (l.employeeUsername || '').toLowerCase().includes(q) ||
        String(l.employeeId || '').includes(q) ||
        (l.leaveType || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [allLeaves, activeTab, searchTerm]);

  /* ── Filtered team list ───────────────────────────────────── */
  const filteredSubs = React.useMemo(() => {
    if (!empSearch) return subordinates;
    const q = empSearch.toLowerCase();
    return subordinates.filter(e =>
      (e.fullName || '').toLowerCase().includes(q) ||
      (e.employeeId || '').toLowerCase().includes(q) ||
      (e.department || '').toLowerCase().includes(q) ||
      (e.jobTitle || '').toLowerCase().includes(q)
    );
  }, [subordinates, empSearch]);

  /* ── Actions ──────────────────────────────────────────────── */
  const openConfirm = (leave, status) => {
    setActionRemark('');
    setConfirm({ open: true, leave, status, loading: false });
  };

  const handleAction = async () => {
    const { leave, status } = confirm;
    if (!leave) return;
    setConfirm(p => ({ ...p, loading: true }));
    try {
      if (status === 'REVERSE') {
        await api.put(`/leave/${leave.id}/reverse-rejection`, {});
        toast.success('Rejection reversed successfully');
      } else {
        const remarks = actionRemark.trim() || (status === 'APPROVED' ? 'Approved by Manager' : 'Rejected by Manager');
        await api.put('/leave/approve', { leaveId: leave.id, status, remarks });
        toast.success(`Leave ${status.toLowerCase()} successfully`);
      }
      setConfirm({ open: false, leave: null, status: null, loading: false });
      fetchLeaves();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed. Please try again.');
      setConfirm(p => ({ ...p, loading: false }));
    }
  };

  const TABS = [
    { key: 'PENDING',  label: 'Pending',  count: counts.PENDING },
    { key: 'APPROVED', label: 'Approved', count: counts.APPROVED },
    { key: 'REJECTED', label: 'Rejected', count: counts.REJECTED },
  ];

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, leave: null, status: null, loading: false })}
        onConfirm={handleAction}
        loading={confirm.loading}
        title={
          confirm.status === 'APPROVED' ? 'Approve Leave'
          : confirm.status === 'REVERSE' ? 'Reverse Rejection'
          : 'Reject Leave'
        }
        message={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ fontSize: '14px', color: '#374151' }}>
              {confirm.status === 'APPROVED'
                ? `Approve leave for ${confirm.leave?.employeeName || confirm.leave?.employeeUsername}?`
                : confirm.status === 'REVERSE'
                ? `Reverse the rejection for ${confirm.leave?.employeeName || confirm.leave?.employeeUsername}? This will restore the application to PENDING status and remove the LOP deduction.`
                : `Reject leave for ${confirm.leave?.employeeName || confirm.leave?.employeeUsername}? Loss of Pay (LOP) will apply.`}
            </p>
            {confirm.status !== 'REVERSE' && (
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                  Remarks (optional)
                </label>
                <input
                  type="text"
                  placeholder="Add a note…"
                  value={actionRemark}
                  onChange={e => setActionRemark(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
                />
              </div>
            )}
          </div>
        }
        confirmText={confirm.status === 'APPROVED' ? 'Approve' : confirm.status === 'REVERSE' ? 'Reverse' : 'Reject'}
        variant={confirm.status === 'APPROVED' ? 'info' : confirm.status === 'REVERSE' ? 'warning' : 'danger'}
      />


      {/* ══════ PAGE HEADER ══════ */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5 flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4">
        <div className="flex items-center justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate('/EmpDashboard')}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-sky-600 transition-colors mb-1.5 cursor-pointer group"
            >
              <FiArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              <span>Back to Dashboard</span>
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">Team Leave Management</h1>
          </div>

          <button
            onClick={() => { fetchSubordinates(); fetchLeaves(); }}
            disabled={loadingLeaves || loadingSubs}
            className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-slate-600 cursor-pointer shrink-0"
            title="Refresh data"
          >
            <FiRefreshCw size={14} className={(loadingLeaves || loadingSubs) ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Header Segmented Slider & Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-wrap">
          {/* Segmented Sider / Slider: Leaves | My Team */}
          <div className="flex items-center p-1 bg-gray-100 rounded-xl border border-gray-200 text-xs">
            <button
              type="button"
              onClick={() => setMainView('leaves')}
              className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${
                mainView === 'leaves'
                  ? 'bg-white text-gray-900 font-semibold shadow-xs'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <FiFileText size={13} />
              <span>Leaves</span>
              {counts.PENDING > 0 && (
                <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                  {counts.PENDING}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setMainView('team')}
              className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${
                mainView === 'team'
                  ? 'bg-white text-gray-900 font-semibold shadow-xs'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <FiUsers size={13} />
              <span>My Team</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-medium bg-gray-200 text-gray-700">
                {subordinates.length}
              </span>
            </button>
          </div>

          <div className="grid grid-cols-2 sm:flex items-center gap-2">
            <button
              onClick={() => openCreditEarned(null)}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold text-white bg-[#008cd6] hover:bg-[#0070b0] transition-colors shadow-xs cursor-pointer"
            >
              <FiAward size={14} /> <span className="truncate">Add Earned</span>
            </button>

            <button
              onClick={() => openAddLeave(null)}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold text-white bg-[#008cd6] hover:bg-[#0070b0] transition-colors cursor-pointer"
            >
              <FiPlus size={14} /> <span>Add Leave</span>
            </button>
          </div>

          <button
            onClick={() => { fetchSubordinates(); fetchLeaves(); }}
            disabled={loadingLeaves || loadingSubs}
            className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-slate-600 cursor-pointer"
            title="Refresh data"
          >
            <FiRefreshCw size={13} className={(loadingLeaves || loadingSubs) ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ══════ MY TEAM ══════ */}
      {mainView === 'team' && (
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {/* Team header */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FiUsers size={16} className="text-[#008cd6]" />
            <h2 className="font-bold text-gray-900 text-sm">My Team</h2>
            <span className="px-2 py-0.5 rounded-full text-xs font-bold border" style={{ background: '#f0f9ff', color: '#008cd6', borderColor: '#bae6fd' }}>
              {subordinates.length}
            </span>
          </div>
          <div className="relative w-full sm:w-auto">
            <FiSearch size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search team…"
              value={empSearch}
              onChange={e => setEmpSearch(e.target.value)}
              className="w-full sm:w-[200px] pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-[#008cd6]"
            />
          </div>
        </div>

        {/* Team list */}
        {loadingSubs ? (
          <div className="flex items-center justify-center gap-2 py-8 text-gray-400">
            <FiRefreshCw className="animate-spin" size={14} />
            <span className="text-sm">Loading team…</span>
          </div>
        ) : filteredSubs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
            <FiUsers size={24} className="text-gray-300" />
            <p className="text-sm font-medium text-gray-500">
              {subordinates.length === 0
                ? 'No direct reports assigned to you yet'
                : 'No team members match your search'}
            </p>
          </div>
        ) : (
          <div>
            {/* Table header (Desktop) */}
            <div className="hidden md:grid px-6 py-2.5 grid-cols-12 gap-3 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <span className="col-span-3">Employee</span>
              <span className="col-span-2">Role / Dept</span>
              <span className="col-span-2">Earned Leaves</span>
              <span className="col-span-2">Leave Quota</span>
              <span className="col-span-3 text-right">Actions</span>
            </div>

            {/* Desktop Table Rows */}
            <div className="hidden md:block">
              {filteredSubs.map(emp => {
                const pendingCount = allLeaves.filter(l => l.employeeId === emp.employeeId && l.status === 'PENDING').length;
                const earnedDays = emp.earnedLeave ?? 0;
                const remainingDays = emp.remainingLeave ?? emp.allocated ?? 24;
                const allocatedDays = emp.allocated ?? 24;
                return (
                  <div
                    key={emp.id}
                    className="px-6 py-3.5 grid grid-cols-12 gap-3 items-center border-b border-gray-50 hover:bg-sky-50/40 transition-colors last:border-0 text-xs"
                  >
                    {/* Name */}
                    <div className="col-span-3 flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0"
                        style={{ background: 'linear-gradient(135deg, #008cd6, #38bdf8)' }}>
                        {initials(emp.fullName || '')}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">{emp.fullName || emp.username}</p>
                        <p className="text-[11px] text-gray-400 font-mono">#{emp.employeeId}</p>
                      </div>
                    </div>

                    {/* Role/Dept */}
                    <div className="col-span-2 min-w-0">
                      <p className="text-gray-700 font-medium truncate">{emp.jobTitle || '—'}</p>
                      <p className="text-gray-400 text-[11px] truncate">{emp.department || '—'}</p>
                    </div>

                    {/* Earned Leaves */}
                    <div className="col-span-2 min-w-0">
                      <div className="flex items-baseline gap-1">
                        <span className="font-bold text-slate-900 text-sm">{earnedDays}</span>
                        <span className="text-slate-500 text-[11px]">day{earnedDays === 1 ? '' : 's'}</span>
                      </div>
                    </div>

                    {/* Leave Quota & Balance */}
                    <div className="col-span-2 min-w-0">
                      <p className="font-bold text-slate-800 text-xs">
                        {remainingDays} / {allocatedDays} left
                      </p>
                      <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                        {pendingCount > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.2 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                            {pendingCount} pending
                          </span>
                        ) : (emp.lopLeavesYear || 0) > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.2 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700">
                            {emp.lopLeavesYear} LOP
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">
                            Paid taken: {emp.paidTakenYear ?? 0}d
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-3 flex justify-end items-center gap-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => navigate(`/manager/employee-leave/${emp.employeeId}`, { state: { emp } })}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-[#008cd6] hover:bg-sky-50 border border-sky-200 transition-colors cursor-pointer"
                        title="View complete leave details & calendar"
                      >
                        <FiBarChart2 size={12} /> Leave Details
                      </button>
                      <button
                        type="button"
                        onClick={() => openCreditEarned(emp)}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold text-sky-700 hover:text-sky-900 hover:bg-sky-50 border border-sky-200 transition-colors cursor-pointer"
                        title="Credit 1 earned leave for subordinate (opens sidebar)"
                      >
                        <FiAward size={12} className="text-[#008cd6]" /> + Earned
                      </button>
                      <button
                        type="button"
                        onClick={() => openAddLeave(emp)}
                        className="px-2 py-1 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-slate-200 transition-colors cursor-pointer"
                        title="Add leave for subordinate (opens sidebar)"
                      >
                        + Leave
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile Cards (Mobile) */}
            <div className="divide-y divide-gray-100 md:hidden">
              {filteredSubs.map(emp => {
                const pendingCount = allLeaves.filter(l => l.employeeId === emp.employeeId && l.status === 'PENDING').length;
                const earnedDays = emp.earnedLeave ?? 0;
                const remainingDays = emp.remainingLeave ?? emp.allocated ?? 24;
                const allocatedDays = emp.allocated ?? 24;
                return (
                  <div key={emp.id} className="p-4 space-y-3">
                    {/* Top Row: Avatar + Name + ID + Role/Dept */}
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0"
                        style={{ background: 'linear-gradient(135deg, #008cd6, #38bdf8)' }}
                      >
                        {initials(emp.fullName || '')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 text-sm truncate">{emp.fullName || emp.username}</p>
                        <p className="text-[11px] text-gray-400 font-mono">#{emp.employeeId} · {emp.jobTitle || emp.department || 'Employee'}</p>
                      </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200/60 rounded-xl p-2.5 text-xs">
                      <div>
                        <span className="text-[10px] uppercase font-bold text-slate-400 block">Earned Leaves</span>
                        <span className="font-bold text-slate-800 text-sm">{earnedDays}</span>
                        <span className="text-slate-500 text-[11px] ml-1">day{earnedDays === 1 ? '' : 's'}</span>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase font-bold text-slate-400 block">Leave Quota</span>
                        <span className="font-bold text-slate-800 text-sm">{remainingDays} / {allocatedDays} left</span>
                      </div>
                      {pendingCount > 0 ? (
                        <div className="col-span-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                            {pendingCount} pending requests
                          </span>
                        </div>
                      ) : (emp.lopLeavesYear || 0) > 0 ? (
                        <div className="col-span-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700">
                            {emp.lopLeavesYear} LOP
                          </span>
                        </div>
                      ) : (
                        <div className="col-span-2 text-[10px] text-slate-400">
                          Paid taken: {emp.paidTakenYear ?? 0}d
                        </div>
                      )}
                    </div>

                    {/* Action buttons (3-col mobile grid) */}
                    <div className="grid grid-cols-3 gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => navigate(`/manager/employee-leave/${emp.employeeId}`, { state: { emp } })}
                        className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold text-[#008cd6] hover:bg-sky-50 border border-sky-200 transition-colors cursor-pointer"
                        title="View complete leave details & calendar"
                      >
                        <FiBarChart2 size={12} /> Details
                      </button>
                      <button
                        type="button"
                        onClick={() => openCreditEarned(emp)}
                        className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold text-sky-700 hover:text-sky-900 hover:bg-sky-50 border border-sky-200 transition-colors cursor-pointer"
                        title="Credit 1 earned leave for subordinate"
                      >
                        <FiAward size={12} className="text-[#008cd6]" /> + Earned
                      </button>
                      <button
                        type="button"
                        onClick={() => openAddLeave(emp)}
                        className="flex items-center justify-center px-2 py-1.5 rounded-lg text-[11px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-slate-200 transition-colors cursor-pointer"
                        title="Add leave for subordinate"
                      >
                        + Leave
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      )}

      {/* ══════ LEAVE REQUESTS ══════ */}
      {mainView === 'leaves' && (
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">

        {/* Tabs + controls */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-1 p-1 rounded-xl border border-gray-200 bg-gray-50 overflow-x-auto max-w-full">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs font-semibold transition-all shrink-0 cursor-pointer"
                style={activeTab === t.key
                  ? { background: 'linear-gradient(135deg, #008cd6, #0ea5e9)', color: '#ffffff' }
                  : { color: '#6b7280' }
                }
              >
                {t.label}
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                  style={activeTab === t.key
                    ? { background: 'rgba(255,255,255,0.25)', color: '#ffffff' }
                    : { background: '#e5e7eb', color: '#6b7280' }
                  }
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-auto">
            <FiSearch size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, type…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full sm:w-[200px] pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-[#008cd6]"
            />
          </div>
        </div>

        {/* Leave list */}
        <div className="p-4" style={{ minHeight: '300px' }}>
          {loadingLeaves ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
              <FiRefreshCw className="animate-spin" size={20} style={{ color: '#008cd6' }} />
              <p className="text-sm">Loading leave requests…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-500">
              <FiAlertCircle size={20} style={{ color: '#ef4444' }} />
              <p className="text-sm">{error}</p>
              <button onClick={fetchLeaves} className="text-xs text-[#008cd6] underline">Try again</button>
            </div>
          ) : filteredLeaves.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#f0f9ff' }}>
                <FiCheckCircle size={20} style={{ color: '#008cd6' }} />
              </div>
              <p className="text-sm font-semibold text-gray-600">
                {activeTab === 'PENDING' ? 'All caught up — no pending requests' : `No ${activeTab.toLowerCase()} leaves`}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredLeaves.map(leave => (
                <LeaveCard
                  key={leave.id}
                  leave={leave}
                  isSuperadmin={isSuperadmin}
                  onOpenDetail={lv => navigate(`/hr/leave/${lv.id}`, { state: { leave: lv } })}
                  onApprove={lv => openConfirm(lv, 'APPROVED')}
                  onReject={lv => openConfirm(lv, 'REJECTED')}
                  onReverseReject={lv => openConfirm(lv, 'REVERSE')}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Add Leave for Subordinate Sidebar */}
      <ManagerAddLeaveModal
        isOpen={addLeaveModalOpen}
        onClose={() => {
          setAddLeaveModalOpen(false);
          setSelectedSubForLeave(null);
        }}
        subordinates={subordinates}
        preSelectedEmp={selectedSubForLeave}
        onSuccess={() => {
          fetchLeaves();
          fetchSubordinates();
        }}
      />

      {/* Credit Earned Leave Modal */}
      <CreditEarnedLeaveModal
        isOpen={creditEarnedModalOpen}
        onClose={() => {
          setCreditEarnedModalOpen(false);
          setSelectedSubForEarned(null);
        }}
        subordinates={subordinates}
        preSelectedEmp={selectedSubForEarned}
        onSuccess={() => {
          fetchLeaves();
          fetchSubordinates();
        }}
      />

      {/* In-page Subordinate Summary Modal */}
      {summaryModalEmp && (
        <SummaryModal
          emp={summaryModalEmp}
          onClose={() => setSummaryModalEmp(null)}
          onAddLeave={(emp) => openAddLeave(emp)}
          onCreditEarnedLeave={(emp) => openCreditEarned(emp)}
        />
      )}

      {/* Earned Leaves Details Modal */}
      {earnedDetailsEmp && (
        <EarnedLeaveDetailsModal
          isOpen={Boolean(earnedDetailsEmp)}
          employee={earnedDetailsEmp}
          onClose={() => setEarnedDetailsEmp(null)}
          onCreditEarnedLeave={(emp) => openCreditEarned(emp)}
        />
      )}
    </div>
  );
};

export default ManagerLeaveApprovals;
